import type { CaseRecord } from '@compass/client/types/case_record';
import { Button } from '@components/ui/button';
import { FieldGroupAccordion } from '@components/ui/field-group-accordion';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import { SimpleTooltip } from '@components/ui/tooltip';
import { ChevronsDownUp, Edit3, Save, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { DispositionSummarySection } from '@/components/DispositionSummarySection';
import type { OutputEditor } from '@/hooks/useOutputEditor';
import { filterOutputGroupsByPaths } from '@/lib/disposition/filterOutputGroups';
import type { FieldGroup } from '@/types/formSchema';
import { cn } from '@/utils';
import {
  createAllAccordionIndices,
  expandAccordionAncestorsAndScrollToField,
  toggleAllAccordionItems,
} from '@/utils/accordion';

/** Output board filter: All / Missed / Required — mirrors the Review/Human Call tabs. */
type OutputFilterValue = 'all' | 'missed' | 'required';

const pillBase =
  'ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-md px-1.5 text-xs font-semibold';
const tabTriggerClass =
  'h-6 px-2 text-xs text-muted-foreground data-[state=active]:text-foreground';

interface OutputBoardEditorProps {
  /** Edit-state machine from `useOutputEditor` (owned by the parent so it can feed disposition). */
  editor: OutputEditor;
  /** Whether the assignee may edit — gates the Edit/Save/Cancel controls. */
  canEdit: boolean;
  /** Base groups to render (before the disposition filter is applied). */
  groups: FieldGroup[];
  /** Disposition required/missing field paths — drive the filter pills + filtering. */
  requiredFields: string[];
  missingFields: string[];
  /** Summary regeneration in flight — disables Edit/Save/Cancel while true. */
  isRegenerating?: boolean;
  title?: string;
  editTestId?: string;
  saveTestId?: string;
  /** Right-aligned content on the title row (e.g. Regenerate Summary, Move to MedBV). */
  headerActions?: React.ReactNode;
  /** Node pinned before the title — e.g. the enclosing sidebar's collapse control. */
  leadingActions?: React.ReactNode;
  /**
   * Pin the title row (title, filter, header actions) to the top of the enclosing scroller,
   * like the Call Script's "All fields" header. Assumes that scroller pads `px-4` and has
   * NO top padding: the row's negative margins cancel the sides so it spans edge to edge,
   * and it carries the top inset itself. A sticky row sticks below its scroller's top
   * padding, so any padding there leaves a strip the fields show through as they scroll.
   */
  stickyHeader?: boolean;
  /** Empty-state copy for the unfiltered ("All") view. */
  allEmptyStateMessage?: { title: string; subtitle: string };
  /** When provided, renders the disposition + case-summary section (workflows with a
   * disposition engine only) between the header and the board. */
  caseId?: string;
  caseDetails?: CaseRecord;
  /** Gate for editing disposition + summary — computed by the caller. */
  canEditDisposition?: boolean;
  /** Pass-through props forwarded to FieldGroupAccordion (disposition source, AI-suggestion
   * wiring, onFieldBlur, workflowType, isHumanAudit, caseRecordId, …). Managed props
   * (groups/data/isEditable/section/fieldError/onDataChange/expanded/emptyState) are set here. */
  fieldProps?: Partial<React.ComponentProps<typeof FieldGroupAccordion>>;
}

/**
 * The shared, editable case Output board (BI-1246). Renders the All/Missed/Required
 * filter, the explicit Edit / Save / Cancel toolbar with the phone/fax validation
 * banner, a collapse-all toggle, and the `FieldGroupAccordion` itself. Edit state,
 * validation, and persistence live in `useOutputEditor`; disposition data (live for
 * the Human Audit tab, persisted for the Summary view) is supplied via props. Used by
 * both CaseSummaryView and HumanAuditTab so the two output sections stay in sync.
 */
export const OutputBoardEditor: React.FC<OutputBoardEditorProps> = ({
  editor,
  canEdit,
  groups,
  requiredFields,
  missingFields,
  isRegenerating = false,
  title = 'Output',
  editTestId,
  saveTestId,
  headerActions,
  leadingActions,
  stickyHeader = false,
  allEmptyStateMessage,
  caseId,
  caseDetails,
  canEditDisposition,
  fieldProps,
}) => {
  const [filter, setFilter] = useState<OutputFilterValue>('all');
  const [expanded, setExpanded] = useState<string[]>(() =>
    createAllAccordionIndices(groups.length),
  );

  const hasDispositionFilter = requiredFields.length > 0;

  const filteredGroups = useMemo<FieldGroup[]>(() => {
    if (!hasDispositionFilter || filter === 'all') return groups;
    const paths = filter === 'missed' ? missingFields : requiredFields;
    return filterOutputGroupsByPaths(groups, new Set(paths));
  }, [groups, filter, hasDispositionFilter, missingFields, requiredFields]);

  // Drop back to "All" if the disposition stops providing required fields.
  useEffect(() => {
    if (!hasDispositionFilter && filter !== 'all') setFilter('all');
  }, [hasDispositionFilter, filter]);

  // Keep every visible group expanded as the filtered set changes.
  useEffect(() => {
    setExpanded(createAllAccordionIndices(filteredGroups.length));
  }, [filteredGroups.length]);

  const allExpanded = filteredGroups.length > 0 && expanded.length === filteredGroups.length;

  // Scroll the board to the first field with a validation error (schema order).
  // Reset the filter + expand all groups first so a filtered/collapsed field is
  // reachable; nested sub-accordions along the field's path are then opened
  // level by level before scrolling.
  const scrollToFirstError = React.useCallback(() => {
    const firstErrorId = Object.keys(editor.fieldErrors)[0];
    if (!firstErrorId) return;
    setFilter('all');
    setExpanded(createAllAccordionIndices(groups.length));
    setTimeout(() => {
      void expandAccordionAncestorsAndScrollToField(firstErrorId);
    }, 500);
  }, [editor.fieldErrors, groups.length]);

  // A blocked Save bumps errorScrollNonce — scroll to the offending field. (Watching
  // fieldErrors directly would also fire on live keystroke validation, which must not scroll.)
  useEffect(() => {
    if (editor.errorScrollNonce > 0) scrollToFirstError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.errorScrollNonce]);

  const emptyStateMessage = useMemo(() => {
    if (hasDispositionFilter && filter === 'missed') {
      return { title: 'No missing fields', subtitle: 'All required information has been captured' };
    }
    if (hasDispositionFilter && filter === 'required') {
      return { title: 'No required fields', subtitle: 'This disposition has no required fields' };
    }
    return allEmptyStateMessage;
  }, [hasDispositionFilter, filter, allEmptyStateMessage]);

  const showControls = canEdit || filteredGroups.length > 0;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'flex min-h-7 flex-wrap items-center justify-between gap-2',
          stickyHeader && 'sticky top-0 z-10 -mx-4 bg-background px-4 pb-2 pt-3',
        )}
      >
        <div className="flex items-center gap-3">
          {leadingActions}
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          {hasDispositionFilter && (
            <Tabs value={filter} onValueChange={v => setFilter(v as OutputFilterValue)}>
              <TabsList className="h-7">
                <TabsTrigger value="all" className={tabTriggerClass}>
                  All
                </TabsTrigger>
                <TabsTrigger value="missed" className={tabTriggerClass}>
                  Missed
                  <span
                    className={`${pillBase} ${
                      missingFields.length === 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}
                  >
                    {missingFields.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="required" className={tabTriggerClass}>
                  Required
                  <span className={`${pillBase} bg-muted text-muted-foreground`}>
                    {requiredFields.length}
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
        {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
      </div>

      {caseId && caseDetails && (
        <DispositionSummarySection
          caseId={caseId}
          caseDetails={caseDetails}
          canEdit={!!canEditDisposition}
        />
      )}

      {showControls && (
        <div className="flex items-center justify-end gap-1">
          {editor.isEditing && editor.hasFieldErrors && (
            <span className="mr-1 flex items-center gap-1 text-xs font-medium text-destructive">
              Validation errors found.
              <button
                type="button"
                onClick={scrollToFirstError}
                className="cursor-pointer font-medium text-destructive underline underline-offset-2 hover:no-underline"
              >
                View
              </button>
            </span>
          )}
          {canEdit &&
            (editor.isEditing ? (
              <>
                <SimpleTooltip content="Save" side="bottom">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={editor.save}
                    disabled={editor.isSaving || isRegenerating}
                    className="h-6 w-6 p-0"
                    aria-label="Save"
                    data-testid={saveTestId}
                  >
                    <Save className="h-3 w-3" />
                  </Button>
                </SimpleTooltip>
                <SimpleTooltip content="Cancel" side="bottom">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={editor.cancelEdit}
                    disabled={editor.isSaving || isRegenerating}
                    className="h-6 w-6 p-0"
                    aria-label="Cancel"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </SimpleTooltip>
              </>
            ) : (
              <SimpleTooltip content="Edit" side="bottom">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={editor.startEdit}
                  disabled={isRegenerating}
                  className="h-6 w-6 p-0"
                  aria-label="Edit"
                  data-testid={editTestId}
                >
                  <Edit3 className="h-3 w-3" />
                </Button>
              </SimpleTooltip>
            ))}
          {filteredGroups.length > 0 && (
            <SimpleTooltip content={allExpanded ? 'Collapse all' : 'Expand all'} side="bottom">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 w-6 shrink-0 p-0"
                aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
                onClick={() =>
                  setExpanded(toggleAllAccordionItems(expanded, filteredGroups.length))
                }
              >
                <ChevronsDownUp className="h-3 w-3" />
              </Button>
            </SimpleTooltip>
          )}
        </div>
      )}

      <FieldGroupAccordion
        {...fieldProps}
        groups={filteredGroups}
        data={editor.workingOutput}
        section="output"
        isEditable={canEdit && editor.isEditing}
        fieldError={editor.fieldErrors}
        onDataChange={editor.handleDataChange}
        showCollapseAll={false}
        externalExpandedItems={expanded}
        onExpandedItemsChange={setExpanded}
        emptyStateMessage={emptyStateMessage}
      />
    </div>
  );
};
